import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Container, Row, Col, Button, InputGroup, Table, Spinner } from 'react-bootstrap';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { DataContext } from '../../adapter';
import { ClaimItemDeleteService } from '../../domain';

const StyledContainer = styled(Container)`
    font-family: 'Montserrat', sans-serif;
    color: rgba(41, 37, 97, 1);
    background: rgb(234, 222, 208, 1);
    padding: 20px 20%;

    @media (min-width: 300px) and (max-width: 500px) {
        width: 100%;
        padding: 2% 10%;
    }
`;

const Title = styled.h1`
    font-size: 42px;
    font-weight: 700;
    padding: 40px 0px;
`;

const StyledForm = styled(Form)`
    .form-label {
        font-size: 22px;
        font-weight: 500;
        padding: 4px 0px;
    }

    .form-control {
        margin-bottom: 0.5rem;
    }
`;

const StyledSubmitButton = styled(Button)`
    background-color: rgba(176, 17, 22, 1);
    max-width: fit-content;
    padding: 5px 40px;
    border-radius: 25px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 20px;
    color: white;
    text-align: center;

    @media (min-width: 300px) and (max-width: 500px) {
        font-size: 16px;
        padding: 5px 20px;
    }
`;

const StyledSubmitButton2 = styled(Button)`
    background-color: rgba(176, 17, 22, 1);
    max-width: fit-content;
    padding: 5px 60px;
    border-radius: 25px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 20px;
    color: white;
    text-align: center;

    @media (min-width: 300px) and (max-width: 500px) {
        font-size: 16px;
        padding: 5px 20px;
    }
`;

const Loading = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(0, 0, 0, 0.5);
    z-index: 2000;
`;

const TableContainer = styled.div`
    overflow-x: auto;
    white-space: nowrap;
    padding: 0;
    margin: 0;
    width: 100%;
`;

export default function Details() {
    const { claimsData, updateClaimsData, clearClaimsData } = DataContext();
    const [loading, setIsLoading] = useState(false);
    const [data, setData] = useState([
        {
            item: '',
            brand: '',
            specification: '',
            purchaseYear: '',
            purchaseAmount: ''
        }
    ]);
    const [editIdx, setEditIdx] = useState(-1);
    const [form, setForm] = useState({
        incidentDetails: '',
        incidentLocation: '',
        incidentDate: '',
        claimItems: data
    });
    const [errors, setErrors] = useState({
        incidentDetails: '',
        incidentLocation: '',
        incidentDate: ''
    });
    const navigate = useNavigate();

    const _ClaimItemDeleteService = new ClaimItemDeleteService();

    useEffect(() => {
        const formatDate = (dateString) => {
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
        };

        // Populate form and data state with values from claimsData
        if (claimsData.data) {
            setForm({
                incidentDetails: claimsData.data.incidentDetails || '',
                incidentLocation: claimsData.data.incidentLocation || '',
                incidentDate: claimsData.data.incidentDate || '',
                claimItems: claimsData.data.claimItems || data
            });
            setData(claimsData.data.claimItems || data);
        }

        if (claimsData && claimsData.claimsPayload) {
            const matchingClaim = claimsData.claimsPayload;
            setForm((prevForm) => ({
                ...prevForm,
                incidentDetails: matchingClaim.incidentDetails || prevForm.incidentDetails,
                incidentLocation: matchingClaim.incidentLocation || prevForm.incidentLocation,
                incidentDate: matchingClaim.incidentDate
                    ? formatDate(matchingClaim.incidentDate)
                    : prevForm.incidentDate,
                claimItems: matchingClaim.claimItems || data
            }));
            setData(matchingClaim?.claimItems?.length ? matchingClaim.claimItems : []);
        }
    }, [claimsData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prevForm) => ({
            ...prevForm,
            [name]: value
        }));
    };

    const handleRowChange = (e, index) => {
        const { name, value } = e.target;
        let formattedValue = value;

        if (name === 'purchaseYear') {
            // Remove non-numeric characters and limit to 4 digits
            formattedValue = value.replace(/[^0-9]/g, '').slice(0, 4);

            // Get the current year
            const currentYear = new Date().getFullYear();

            // Ensure the formatted value is a valid year and less than or equal to the current year
            if (formattedValue && Number(formattedValue) > currentYear) {
                formattedValue = currentYear.toString(); // Set to current year if input exceeds it
            }
        } else if (name === 'purchaseAmount') {
            formattedValue = value
                .replace(/[^0-9.]/g, '')
                .replace(/(\..*)\./g, '$1')
                .replace(/(?<=\.\d{2})\d/g, '');
        }

        // Update the data state with the formatted value
        const updatedData = data.map((row, idx) => (idx === index ? { ...row, [name]: formattedValue } : row));
        setData(updatedData);
    };

    const addRow = () => {
        const isAnyFieldEmpty = data.some((data) => {
            return !data.item || !data.brand || !data.specification || !data.purchaseYear || !data.purchaseAmount;
        });

        if (isAnyFieldEmpty) {
            Swal.fire({
                text: 'Please fill in all fields before adding a new row.',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'OK'
            });
            return; // Exit the function without adding a new row
        }

        const newRowIndex = data.length;
        setData([...data, { item: '', brand: '', specification: '', purchaseYear: '', purchaseAmount: '' }]);
        setEditIdx(newRowIndex);
    };

    const editRow = (index) => {
        setEditIdx(index);
    };

    const cancelItemEditing = async () => {
        const alertResponse = await Swal.fire({
            title: 'Confirm',
            text: 'Discard edited values?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Ok',
            customClass: {
                popup: 'my-popup',
                confirmButton: 'my-confirm-button',
                cancelButton: 'my-cancel-button'
            }
        });

        if (alertResponse.isConfirmed) {
            setEditIdx(-1);
        }
    };

    const saveRow = (index) => {
        setEditIdx(-1);
    };

    const deleteRow = async (index, claimItemId) => {
        try {
            Swal.fire({
                title: 'Confirm',
                text: 'Delete Item?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Proceed',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                }
            })
                .then(async (result) => {
                    if (result.isConfirmed) {
                        setIsLoading(true);
                        if (claimItemId) {
                            const response = await _ClaimItemDeleteService.execute({ id: claimItemId });
                            if (!response.updatedClaimItems?.length) {
                                Swal.fire({
                                    text: 'Sorry. Something went wrong. Please try again later.',
                                    icon: 'warning',
                                    confirmButtonColor: '#3085d6',
                                    confirmButtonText: 'OK',
                                    customClass: {
                                        popup: 'my-popup',
                                        confirmButton: 'my-confirm-button',
                                        cancelButton: 'my-cancel-button'
                                    }
                                });
                                return;
                            }

                            Swal.fire({
                                text: 'Damaged Item Deleted',
                                icon: 'success',
                                confirmButtonColor: '#3085d6',
                                confirmButtonText: 'OK',
                                customClass: {
                                    popup: 'my-popup',
                                    confirmButton: 'my-confirm-button',
                                    cancelButton: 'my-cancel-button'
                                }
                            });
                        }
                        const updatedData = data.filter((_, idx) => idx !== index);
                        setData(updatedData);
                        setIsLoading(false);
                    }
                })
                .catch((e) => {
                    Swal.fire({
                        text: 'Sorry. Something went wrong. Please try again later.',
                        icon: 'warning',
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: 'OK',
                        customClass: {
                            popup: 'my-popup',
                            confirmButton: 'my-confirm-button',
                            cancelButton: 'my-cancel-button'
                        }
                    });
                    setIsLoading(false);
                });
        } catch (error) {
            Swal.fire({
                text: 'Sorry. Something went wrong. Please try again later.',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'OK',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                }
            });
            setIsLoading(false);
        }
    };

    const handleValidate = () => {
        let isValid = true;
        let newErrors = { ...errors };

        if (!form.incidentDetails) {
            isValid = false;
            newErrors.incidentDetails = 'Incident Details is required.';
        } else {
            newErrors.incidentDetails = '';
        }

        if (!form.incidentLocation) {
            isValid = false;
            newErrors.incidentLocation = 'Incident Location is required.';
        } else {
            newErrors.incidentLocation = '';
        }

        if (!form.incidentDate) {
            isValid = false;
            newErrors.incidentDate = 'Date of Incident is required.';
        } else {
            // Check if the incident date is in the future
            const today = new Date().toISOString().split('T')[0];
            if (new Date(form.incidentDate) > new Date(today)) {
                isValid = false;
                newErrors.incidentDate = 'Incident Date must not be future date.';
            } else {
                newErrors.incidentDate = '';
            }
    
            // Validate against policy dates
            const inceptionDate = claimsData?.availablePolicy[0]?.inceptionDate;
            const expiryDate = claimsData?.availablePolicy[0]?.expiryDate;
            if (inceptionDate && new Date(inceptionDate) > new Date(form.incidentDate)) {
                isValid = false;
                newErrors.incidentDate = 'Incident Date cannot be earlier than the policy effective date.';
            }
    
            if (expiryDate && new Date(expiryDate) < new Date(form.incidentDate)) {
                isValid = false;
                newErrors.incidentDate = 'Incident Date cannot be later than the policy expiry.';
            }

            const effectiveTerminationDate = claimsData?.availablePolicy[0]?.effectiveTerminationDate;

            if (effectiveTerminationDate && new Date(form.incidentDate) >= new Date(effectiveTerminationDate)) {
                isValid = false;
                newErrors.incidentDate = 'Incident Date cannot be on or after the policy termination date.';
            }
        }

        const hasRowErrors = data.some(
            (row) => !row.item || !row.brand || !row.specification || !row.purchaseYear || !row.purchaseAmount
        );

        if (hasRowErrors) {
            Swal.fire({
                text: 'Please fill in all fields for all items.',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'OK',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                }
            });
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (handleValidate()) {
            setIsLoading(true);

            const newClaim = {
                incidentDetails: form.incidentDetails,
                incidentLocation: form.incidentLocation,
                incidentDate: form.incidentDate,
                claimItems: data
            };

            // Ensure existing data is an object
            const existingData = claimsData.data && typeof claimsData.data === 'object' ? claimsData.data : {};

            // Merge new claim with existing data
            const updatedClaimsData = {
                ...claimsData,
                claimsPayload: {
                    ...claimsData.claimsPayload,
                    ...existingData,
                    ...newClaim
                }
            };

            // Update the claims data context
            updateClaimsData(updatedClaimsData);

            setTimeout(() => {
                setIsLoading(false);
                if (!claimsData.claimsPayload.customerDetails.customerId) {
                    if (!data.length) {
                        Swal.fire({
                            text: 'No claim items added. Please add at least one item.',
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonColor: '#3085d6',
                            cancelButtonColor: '#d33',
                            confirmButtonText: 'Ok',
                            customClass: {
                                popup: 'my-popup',
                                confirmButton: 'my-confirm-button',
                                cancelButton: 'my-cancel-button'
                            }
                        });
                        return;
                    }
                    navigate('/kyc');
                } else if (claimsData.claimsPayload.customerDetails.customerId) {
                    if (!data.length) {
                        Swal.fire({
                            text: 'No claim items added. Please add at least one item.',
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonColor: '#3085d6',
                            cancelButtonColor: '#d33',
                            confirmButtonText: 'Ok',
                            customClass: {
                                popup: 'my-popup',
                                confirmButton: 'my-confirm-button',
                                cancelButton: 'my-cancel-button'
                            }
                        });
                        return;
                    }
                    navigate('/documents');
                } else {
                    console.log(claimsData);
                    Swal.fire({
                        text: 'Unexpected value for createNewClaim',
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: '#3085d6',
                        cancelButtonColor: '#d33',
                        confirmButtonText: 'Ok',
                        customClass: {
                            popup: 'my-popup',
                            confirmButton: 'my-confirm-button',
                            cancelButton: 'my-cancel-button'
                        }
                    });
                }
            }, 1000);
        }
    };

    useEffect(() => {
        if (Object.keys(claimsData).length) {
            if (!claimsData?.claimsPayload) {
                navigate('/home');
                clearClaimsData();
            }
        } else {
            navigate('/home');
        }
    }, []);

    return (
        <>
            {loading && (
                <Loading>
                    <Spinner animation="border" variant="light" />
                </Loading>
            )}
            <StyledContainer fluid>
                <Row>
                    <Title fluid>File a Claim</Title>
                </Row>
                <StyledForm>
                    <Row>
                        <Col lg={12}>
                            <Form.Group>
                                <Form.Label>1. Incident Report (What, When, How)</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        name="incidentDetails"
                                        value={form.incidentDetails}
                                        onChange={handleChange}
                                        isInvalid={!!errors.incidentDetails}
                                        aria-label="With textarea"
                                        placeholder='Ibigay ang Incident Report(Ang mga ANO, KAILAN, PAANO ng mga pangyayari)'
                                    />
                                    <Form.Control.Feedback type="invalid"></Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={4}>
                            <Form.Group>
                                <Form.Label>2. Location of Incident:</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        name="incidentLocation"
                                        value={form.incidentLocation}
                                        onChange={handleChange}
                                        isInvalid={!!errors.incidentLocation}
                                        aria-label="With textarea"
                                        placeholder='e.g., "Las Pinas, Metro Manila"'
                                        maxLength={100}
                                    />
                                    <Form.Control.Feedback type="invalid"></Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={4}>
                            <Form.Group>
                                <Form.Label>3. Date of Incident:</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="date"
                                        name="incidentDate"
                                        value={form.incidentDate}
                                        onChange={handleChange}
                                        isInvalid={!!errors.incidentDate}
                                        aria-label="With textarea"
                                        placeholder="mm/dd/yyyy"
                                        max={new Date().toISOString().split('T')[0]}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.incidentDate}</Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={12}>
                            <Form.Group>
                                <Form.Label>4. List of Damaged Items:</Form.Label>
                                <Row>
                                    <Col lg={12} className="d-flex justify-content-end mb-2">
                                        <Button size="sm" onClick={addRow}>
                                            <i className="bi bi-plus-lg"> Add Item </i>
                                        </Button>
                                    </Col>
                                </Row>
                                <TableContainer>
                                    <Table striped bordered hover style={{ overflow: 'auto' }}>
                                        <thead className="text-center">
                                            <tr>
                                                <th></th>
                                                <th>Damaged Items</th>
                                                <th>Brand</th>
                                                <th>Product Specification</th>
                                                <th>Year Purchased</th>
                                                <th>Amount Purchased</th>
                                                {!!data?.filter((item) => item.remarks)?.length && <th>Remarks</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.map((row, index) => (
                                                <tr key={index}>
                                                    <td width="1">
                                                        {editIdx === index ? (
                                                            <div className="d-flex flex-row">
                                                                <Button
                                                                    variant="success"
                                                                    size="sm"
                                                                    className="rounded-pill me-1"
                                                                    onClick={() => saveRow(index)}
                                                                >
                                                                    <i className="bi bi-check-lg"></i>
                                                                </Button>
                                                                {/* <Button
                                                                    type="button"
                                                                    className="btn btn-warning rounded-pill btn-sm"
                                                                    onClick={cancelItemEditing}
                                                                >
                                                                    <i className="bi bi-x-circle"></i>
                                                                </Button> */}
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    className="rounded-pill"
                                                                    onClick={() => deleteRow(index, row.id)}
                                                                >
                                                                    <i className="bi bi-trash-fill"></i>
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <div className="d-flex flex-row">
                                                                <Button
                                                                    variant="warning"
                                                                    size="sm"
                                                                    className="rounded-pill me-1"
                                                                    onClick={() => editRow(index)}
                                                                >
                                                                    <i className="bi bi-pencil-fill"> </i>
                                                                </Button>
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    className="rounded-pill"
                                                                    onClick={() => deleteRow(index, row.id)}
                                                                >
                                                                    <i className="bi bi-trash-fill"></i>
                                                                </Button>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            name="item"
                                                            value={row.item}
                                                            onChange={(e) => handleRowChange(e, index)}
                                                            disabled={editIdx !== index}
                                                            placeholder='e.g Smart TV'
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            name="brand"
                                                            value={row.brand}
                                                            onChange={(e) => handleRowChange(e, index)}
                                                            disabled={editIdx !== index}
                                                            placeholder='e.g Devant'
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            name="specification"
                                                            value={row.specification}
                                                            onChange={(e) => handleRowChange(e, index)}
                                                            disabled={editIdx !== index}
                                                            placeholder='e.g Model No. 55UHD204'
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            name="purchaseYear"
                                                            value={row.purchaseYear}
                                                            onChange={(e) => handleRowChange(e, index)}
                                                            disabled={editIdx !== index}
                                                            placeholder='e.g 2018'
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            name="purchaseAmount"
                                                            value={row.purchaseAmount}
                                                            onChange={(e) => handleRowChange(e, index)}
                                                            disabled={editIdx !== index}
                                                            placeholder='e.g ₱ 24,000'
                                                        />
                                                    </td>
                                                    {row?.remarks?.length && <td>{row.remarks}</td>}
                                                </tr>
                                            ))}

                                            {!data.length && (
                                                <tr>
                                                    <td colSpan="6" className="text-center">
                                                        No items yet.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </TableContainer>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <div className="gap-2 justify-content-left mt-2">
                            <StyledSubmitButton
                                className="btn-submit me-2"
                                variant="secondary"
                                type="button"
                                onClick={() => {
                                    if (typeof claimsData.selectedClaim !== 'number') {
                                        navigate('/selection');
                                    } else {
                                        navigate('/home');
                                    }
                                }}
                            >
                                PREVIOUS
                            </StyledSubmitButton>
                            <StyledSubmitButton2 variant="secondary" type="submit" onClick={handleFormSubmit}>
                                NEXT
                            </StyledSubmitButton2>
                        </div>
                    </Row>
                </StyledForm>
            </StyledContainer>
        </>
    );
}
